import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

import type { TagResource } from "@/types_api/backend/transport/list_organization_tags_response"

import { TagChip } from "./TagChip"

interface DeleteTagDialogProps {
  tag: TagResource
  assignmentCount: number | null
  isLoading: boolean
  isPending: boolean
  isCatalogReady: boolean
  catalogError: string | null
  onRetryCatalog: () => void
  previewError: string | null
  error: string | null
  onRetry: () => void
  onClose: () => void
  onConfirm: () => void
}

export function DeleteTagDialog({
  tag,
  assignmentCount,
  isLoading,
  isPending,
  isCatalogReady,
  catalogError,
  onRetryCatalog,
  previewError,
  error,
  onRetry,
  onClose,
  onConfirm,
}: DeleteTagDialogProps) {
  const canDelete =
    isCatalogReady &&
    assignmentCount !== null &&
    !isLoading &&
    !previewError &&
    !isPending
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <DialogContent size="md" data-testid="delete-tag-dialog">
        <DialogHeader>
          <DialogTitle>Delete Tag?</DialogTitle>
          <DialogDescription>
            Delete this tag and remove all its assignments. Your resources will
            remain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-6 py-4">
          <TagChip tag={tag.name} color={tag.color} />
          {isLoading ? (
            <p role="status" className="text-sm text-coval-text-muted">
              Checking tag usage…
            </p>
          ) : previewError ? (
            <Alert variant="danger">
              <AlertDescription>
                {previewError}{" "}
                <Button
                  variant="secondary"
                  onClick={onRetry}
                  disabled={isPending}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            assignmentCount !== null && (
              <div className="space-y-2 text-sm">
                <p>
                  This tag is assigned to{" "}
                  <strong>
                    {assignmentCount.toLocaleString()}{" "}
                    {assignmentCount === 1 ? "resource" : "resources"}
                  </strong>
                  , including any inactive resources.
                </p>
                <p className="text-coval-text-muted">
                  All assignments will be removed. Reusing this name won’t
                  restore them.
                </p>
              </div>
            )
          )}
          {catalogError && (
            <Alert variant="danger">
              <AlertDescription>
                {catalogError}{" "}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isPending}
                  onClick={onRetryCatalog}
                >
                  Retry catalog
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="danger">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={isPending}
            disabled={!canDelete}
            onClick={() => {
              if (canDelete) {
                onConfirm()
              }
            }}
          >
            Delete Tag
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
